import type { GameState, StatusEffects } from '../types';
import { appendEvent } from '../event-log';

/**
 * Create a fresh StatusEffects object with all effects inactive.
 */
export function createEmptyStatusEffects(): StatusEffects {
  return {
    stunnedUntil: null,
    frozenUntil: null,
    toxicCloudAffected: null,
    phoenixBuff: null,
    stoneSkin: { active: false },
  };
}

/**
 * Apply a status effect to a player.
 * Emits `status_effect_applied` event (event type added to union in Task 2.1).
 *
 * `duration` is number of turns; the expiry round is computed as state.round + duration.
 * For 'stoneSkin', the manager sets `statusEffects.stoneSkin.active = true` (no duration).
 *
 * @throws Error if player not found
 */
export function applyStatusEffect(
  state: GameState,
  playerId: string,
  effectType: 'stunned' | 'frozen' | 'toxicCloud' | 'phoenixBuff' | 'stoneSkin',
  duration: number,
  source: string,
  extra?: { hpBonus?: number; damagePerTick?: number },
): void {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  const expiresAt = state.round + duration;

  switch (effectType) {
    case 'stunned':
      player.statusEffects.stunnedUntil = expiresAt;
      break;
    case 'frozen':
      player.statusEffects.frozenUntil = expiresAt;
      break;
    case 'stoneSkin':
      player.statusEffects.stoneSkin.active = true;
      break;
    case 'phoenixBuff':
      player.statusEffects.phoenixBuff = {
        hpBonus: extra?.hpBonus ?? 0,
        expiresAt,
      };
      break;
    case 'toxicCloud':
      player.statusEffects.toxicCloudAffected = {
        expiresAt,
        damagePerTick: extra?.damagePerTick ?? 0,
      };
      break;
  }

  appendEvent(state, {
    type: 'status_effect_applied',
    playerId,
    effectType,
    duration,
    source,
  });
}

/**
 * Tick active effects for a player at the START of their turn.
 * Clears effects that have expired (expiresAt < state.round) and emits
 * `status_effect_expired` for each cleared effect.
 * Effects exactly at state.round are still active this turn; they expire
 * at the start of the next round.
 *
 * Returns flags so executeTurn can skip the roll if frozen/stunned.
 *
 * @throws Error if player not found
 */
export function tickStatusEffects(
  state: GameState,
  playerId: string,
): { frozen: boolean; stunned: boolean } {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  const { statusEffects } = player;
  let frozen = false;
  let stunned = false;

  // --- frozen ---
  if (statusEffects.frozenUntil !== null) {
    if (statusEffects.frozenUntil < state.round) {
      // Expired: clear it and emit
      statusEffects.frozenUntil = null;
      appendEvent(state, {
        type: 'status_effect_expired',
        playerId,
        effectType: 'frozen',
      });
    } else {
      // Still active this round (frozenUntil === state.round or future)
      frozen = true;
    }
  }

  // --- stunned ---
  if (statusEffects.stunnedUntil !== null) {
    if (statusEffects.stunnedUntil < state.round) {
      // Expired: clear it and emit
      statusEffects.stunnedUntil = null;
      appendEvent(state, {
        type: 'status_effect_expired',
        playerId,
        effectType: 'stunned',
      });
    } else {
      // Still active this round
      stunned = true;
    }
  }

  // --- toxicCloudAffected ---
  if (statusEffects.toxicCloudAffected !== null) {
    if (statusEffects.toxicCloudAffected.expiresAt < state.round) {
      statusEffects.toxicCloudAffected = null;
      appendEvent(state, {
        type: 'status_effect_expired',
        playerId,
        effectType: 'toxicCloud',
      });
    }
  }

  // --- phoenixBuff ---
  // Legacy save compatibility: Phoenix Potion overheal is now represented as real HP above 30,
  // not as a timed status. Clear stale buff metadata without changing HP.
  if (statusEffects.phoenixBuff !== null) {
    statusEffects.phoenixBuff = null;
    appendEvent(state, {
      type: 'status_effect_expired',
      playerId,
      effectType: 'phoenixBuff',
    });
  }

  return { frozen, stunned };
}

/**
 * Return true when freeze is active for this player's current active turn.
 * Stale round-based freeze values are not considered active here; the normal
 * status tick owns stale expiry events.
 */
export function isFrozenActive(state: GameState, playerId: string): boolean {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);
  const { frozenUntil } = player.statusEffects;
  return frozenUntil !== null && frozenUntil >= state.round;
}

/**
 * Consume the active freeze movement block. Idempotent: if freeze is already
 * absent or stale, no event is emitted.
 */
export function consumeFrozenMovementBlock(state: GameState, playerId: string): boolean {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  if (!isFrozenActive(state, playerId)) {
    return false;
  }

  player.statusEffects.frozenUntil = null;
  appendEvent(state, {
    type: 'status_effect_expired',
    playerId,
    effectType: 'frozen',
  });
  return true;
}

/**
 * Check if a player currently has an active Stone Skin shield.
 *
 * @throws Error if player not found
 */
export function hasStoneSkin(state: GameState, playerId: string): boolean {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);
  return player.statusEffects.stoneSkin.active;
}

/**
 * Consume the player's Stone Skin shield.
 * Sets stoneSkin.active = false and emits `status_effect_expired` with effectType='stoneSkin'.
 * Idempotent: no-op (no event) if Stone Skin is not active.
 *
 * @throws Error if player not found
 */
export function consumeStoneSkin(state: GameState, playerId: string): void {
  const player = state.players.get(playerId);
  if (!player) throw new Error(`Player not found: ${playerId}`);

  if (!player.statusEffects.stoneSkin.active) {
    // Idempotent no-op
    return;
  }

  player.statusEffects.stoneSkin.active = false;
  appendEvent(state, {
    type: 'status_effect_expired',
    playerId,
    effectType: 'stoneSkin',
  });
}
